import {
  Box,
  Button,
  CardActions,
  Checkbox,
  FormControlLabel,
  Menu,
  MenuItem,
  Tooltip,
} from "@mui/material";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { gql } from "@apollo/client";

import { isVisitCancelable } from "@caredna-marlin/utils/rules/visitStatus";
import { Permission, useHasPermission } from "~/hooks/permissions";
import PermissionOrHide from "../common/PermissionOrHide";

import { useVisitStatusAdvancer } from "~/hooks/useVisitStatusAdvancer";
import { PartialVisit } from "~/utils/visitsHelper";
import { useCallTimer } from "~/hooks/useCallTimer";
import { useCurrentOrg } from "~/providers/GraphqlProvider";
import { VisitResourceVirtualProviderStatusEnum, useSetVisitManualEditsMutation } from "~/graphql/__generated__/graphql";
import DebugSetNotifiedButton from "./DebugSetNotifiedButton";

gql(`
  mutation SetVisitManualEdits($visitId: Int!, $manualEdits: Boolean!)
  @permission(requires: manageAnyVisit, check: [userInVisitServiceLine])
  {
    updateVisit(
      where: { id: { eq: $visitId } }
      set: { manualEdits: $manualEdits }
    ) {
      id
      manualEdits
    }
  }
`);

type Props = {
  visit: PartialVisit,
  onCancel: (visitId: number) => void;
  onEdit: (visitId: number) => void;
  isLoading: boolean;
}

const VisitPopupActions = ({ visit, isLoading, onEdit, onCancel }: Props) => {
  const [editMenuAnchorEl, setEditMenuAnchorEl] = useState<null | HTMLElement>(null);
  const editClose = () => setEditMenuAnchorEl(null);
  const org = useCurrentOrg()

  const canCreatedOverrideVisit = useHasPermission(Permission.createOverrideVisits);
  const canCreateDragEvents = useHasPermission(Permission.createDragEvents);
  const canEditVisitDetails = canCreateDragEvents || canCreatedOverrideVisit;

  const progressAsProvider = useVisitStatusAdvancer(visit);
  const canImpersonate = useHasPermission(Permission.impersonateAnyProvider)
  const canModify = visit.eventStatus && isVisitCancelable(visit.eventStatus) && (
    canImpersonate || org?.serviceLineIds.includes(visit.episode?.serviceLineId!)
  )

  // Check if current provider is in an active call
  const providerId = useCurrentOrg()?.providerId;
  const currentProviderResource = visit.visitResources?.find(vr => vr.providerId === providerId);
  const isInCall = currentProviderResource?.virtualProviderStatus === VisitResourceVirtualProviderStatusEnum.Joined;

  // Track call duration when in active call
  const callDuration = useCallTimer(isInCall);

  // Determine button label with timer if in call
  let buttonLabel = progressAsProvider.label;
  if (isInCall && buttonLabel === "Call Complete") {
    buttonLabel = `In Call (${callDuration})`;
  }

  const [locked, setLocked] = useState(visit.manualEdits ?? false);
  useEffect(() => { setLocked(visit.manualEdits ?? false); }, [visit.manualEdits]);

  const [setManualEdits, { loading: savingLock }] = useSetVisitManualEditsMutation();

  const handleToggleLock = async () => {
    const next = !locked;
    setLocked(next);
    try {
      const result = await setManualEdits({
        variables: { visitId: visit.id, manualEdits: next },
      });
      const updatedVisit = result.data?.updateVisit?.[0];
      if (updatedVisit) {
        setLocked(updatedVisit.manualEdits);
      }
    } catch {
      setLocked(!next);
    }
  };

  if (!canModify) {
    return null
  }

  return <CardActions disableSpacing sx={{ borderTop: 1, borderColor: "divider" }}>
    <Box sx={{ flex: 1 }}>
      {canModify && <PermissionOrHide permission={Permission.manageAnyVisit}>
        {visit.careRequirementId != null && (
          <Tooltip title="Prevents the auto-scheduler, schedule optimizer, and other automation features from moving this visit">
            <FormControlLabel
              control={
                <Checkbox
                  checked={locked}
                  onChange={handleToggleLock}
                  disabled={savingLock}
                  size="small"
                />
              }
              label="Prevent automatic rescheduling"
              slotProps={{ typography: { variant: "body2" } }}
            />
          </Tooltip>
        )}
      </PermissionOrHide>}
    </Box>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, justifyContent: "flex-end" }}>
      {canModify && <PermissionOrHide permission={Permission.manageAnyVisit}>
        {visit.eventStatus && isVisitCancelable(visit.eventStatus) && (<>
          <Button onClick={() => onCancel(visit.id)} color="error">
            Cancel Visit
          </Button>
          {!canEditVisitDetails && <Link to={`/scheduler/visit/${visit.id}`}>
            <Button color="primary" variant="contained">
              Reschedule
            </Button>
          </Link>}
          {canEditVisitDetails && <>
            <Button
              color="primary"
              variant="contained"
              onClick={event => setEditMenuAnchorEl(event.currentTarget)}
            >
              Reschedule
            </Button>
            <Menu
              anchorEl={editMenuAnchorEl}
              open={!!editMenuAnchorEl}
              onClose={editClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <MenuItem component={Link} to={`/scheduler/visit/${visit.id}`} onClick={editClose}>
                View options
              </MenuItem>
              <MenuItem disabled={isLoading} onClick={() => {
                editClose();
                onEdit(visit.id);
              }}>
                Edit manually
              </MenuItem>
            </Menu>
          </>}
        </>)}
      </PermissionOrHide>}
      {progressAsProvider.label && <Tooltip title={progressAsProvider.disallowReason}>
        <div><Button
          onClick={() => progressAsProvider.progressVisit()}
          color={isInCall ? "success" : "primary"}
          variant="contained"
          disabled={!!progressAsProvider.disallowReason || isInCall}
          loading={progressAsProvider.loading}
          startIcon={isInCall ? (
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: 'success.light',
                animation: 'pulse 2s infinite',
                '@keyframes pulse': {
                  '0%': { opacity: 1 },
                  '50%': { opacity: 0.5 },
                  '100%': { opacity: 1 },
                }
              }}
            />
          ) : undefined}
        >
          {buttonLabel}
        </Button></div>
      </Tooltip>}
      {currentProviderResource && (
        <DebugSetNotifiedButton
          visitResourceId={currentProviderResource.id}
          visitId={visit.id}
          currentStatus={currentProviderResource.virtualProviderStatus}
        />
      )}
    </Box>
  </CardActions>
}

export default VisitPopupActions;
